import React, {
  FC,
  useEffect,
  useMemo,
  useRef,
  useState,
  KeyboardEvent,
} from 'react';
import { useOnClickOutside } from 'usehooks-ts';
import {
  CategorizedList,
  GroupByConfig,
  GroupTooltipConfig,
  Option,
} from '../../../types/SelectInput';
import TranslationIcon from '../../atoms/TranslationIcon';
import Caret from '../../atoms/Caret';
import styles from './MenuDropDown.module.css';
import SearchInput from '../SearchInput';
import { translate } from '../../../localization';
import GroupByListView from '../GroupByListView';
import MenuListView from '../MenuListView';
import { categorizeOptions } from '../../../utils/arrayToGroup';

type MenuDropDownProps = {
  list: Option[];
  isOpen: boolean;
  isSorted?: boolean;
  selectedValue: string | string[];
  valueProperty: string;
  sortBy?: string;
  groupingConfig?: GroupByConfig[];
  groupTooltips?: GroupTooltipConfig;
  unCategorizedListTitle?: string;
  isDisabled?: boolean;
  onChange: (value: string | string[]) => void;
  onSelectTabClick?: () => void;
  isShowSelectElement?: boolean;
  className?: string;
  wrapperClasses?: string;
  searchInputPlaceholder?: string;
  multiSelect?: boolean;
  categorizedDropdown?: boolean;
  enableSelectAll?: boolean;
  isSearchable?: boolean;
};

type DropDownRendererProps = {
  list: Option[];
  selectedValue: string | string[];
  groupingConfig?: GroupByConfig[];
  groupTooltips?: GroupTooltipConfig;
  unCategorizedListTitle?: string;
  valueProperty: string;
  onChange: (value: string | string[]) => void;
  multiSelect?: boolean;
  searchInputPlaceholder?: string;
  categorizedDropdown?: boolean;
  enableSelectAll?: boolean;
  isSearchable?: boolean;
};

const DropDownRenderer: FC<DropDownRendererProps> = React.memo(
  ({
    selectedValue,
    list,
    groupingConfig,
    groupTooltips,
    unCategorizedListTitle = translate('commonly_used'),
    valueProperty,
    onChange,
    searchInputPlaceholder = '',
    multiSelect = false,
    categorizedDropdown = false,
    enableSelectAll = false,
    isSearchable = true,
  }) => {
    const [searchTextValue, setSearchTextValue] = useState<string>('');

    const filterList = useMemo(() => {
      if (!isSearchable) {
        return list;
      }
      return list.filter(
        ({ label }) =>
          typeof label === 'string' &&
          label.toLowerCase().includes(searchTextValue.toLowerCase())
      );
    }, [searchTextValue, list, isSearchable]);

    const groupedList: CategorizedList[] = useMemo(() => {
      const config = groupingConfig || [];
      const categorizedOptions = categorizeOptions(
        list,
        config,
        valueProperty,
        unCategorizedListTitle
      );
      // Filter out groups that don't have any items
      return categorizedOptions.filter(
        group => group.list && group.list.length > 0
      );
    }, [list, groupingConfig, valueProperty, unCategorizedListTitle]);

    const handleDropDownChange = (value: string | string[]) => {
      onChange(value);
    };

    const handleSearchChange = (value: string) => {
      setSearchTextValue(value);
    };

    const handleSearchReset = () => {
      setSearchTextValue('');
    };

    const shouldShowFilteredList = filterList.length > 0;
    const shouldShowNoResults =
      searchTextValue !== '' && filterList.length === 0;

    const hasOptionSelected = (option: string) => {
      if (multiSelect) {
        return (
          (selectedValue as string[])?.find(value => value === option) !==
          undefined
        );
      }
      return option === selectedValue;
    };

    return (
      <>
        {isSearchable && (
          <div className={styles['menu-dropdown-renderer__search-input']}>
            <SearchInput
              value={searchTextValue}
              onChange={handleSearchChange}
              onReset={handleSearchReset}
              placeHolder={searchInputPlaceholder}
            />
          </div>
        )}

        {categorizedDropdown && searchTextValue === '' && (
          <GroupByListView
            list={groupedList}
            valueProperty={valueProperty}
            onChange={handleDropDownChange}
            multiSelect={multiSelect}
            hasOptionSelected={hasOptionSelected}
            groupTooltips={groupTooltips}
            enableSelectAll={enableSelectAll}
            selectedValue={selectedValue}
          />
        )}

        {(!categorizedDropdown || searchTextValue) &&
          shouldShowFilteredList && (
            <MenuListView
              list={filterList}
              searchTextValue={searchTextValue}
              valueProperty={valueProperty}
              onChange={handleDropDownChange}
              multiSelect={multiSelect}
              hasOptionSelected={hasOptionSelected}
              enableSelectAll={enableSelectAll}
              selectedValue={selectedValue}
            />
          )}

        {isSearchable && shouldShowNoResults && (
          <div
            className={
              styles['menu-dropdown-renderer__list-wrapper__not-found']
            }
          >
            {translate('search_input_no_results_found')}
          </div>
        )}
      </>
    );
  }
);

DropDownRenderer.displayName = 'DropDownRenderer';

const MenuDropDown: FC<MenuDropDownProps> = React.memo(
  ({
    selectedValue,
    sortBy = '',
    list,
    valueProperty = 'value',
    isOpen,
    isSorted = false,
    groupingConfig,
    groupTooltips,
    unCategorizedListTitle = translate('others'),
    isDisabled = false,
    onChange,
    onSelectTabClick,
    isShowSelectElement = true,
    className = '',
    wrapperClasses = '',
    searchInputPlaceholder = '',
    multiSelect = false,
    categorizedDropdown = false,
    enableSelectAll = false,
    isSearchable = true,
  }) => {
    const menuDownRef = useRef<HTMLDivElement>(null);
    const dropDownRef = useRef<HTMLDivElement>(null);

    const handleMenuDropDownChange = (value: string | string[]) => {
      onChange(value);
    };

    const handleOutsideClicked = () => {
      if (isOpen) {
        onSelectTabClick?.();
      }
    };

    const handleSelectTabClick = () => {
      onSelectTabClick?.();
    };

    const sortedList = useMemo(() => {
      if (isSorted) {
        return [...list].sort((a, b) => {
          const aValue = a[sortBy];
          const bValue = b[sortBy];

          // Handle undefined values explicitly
          if (aValue === undefined && bValue !== undefined) return 1;
          if (aValue !== undefined && bValue === undefined) return -1;
          if (aValue === undefined && bValue === undefined) return 0;

          // Compare strings
          if (typeof aValue === 'string' && typeof bValue === 'string') {
            return aValue.localeCompare(bValue);
          }

          // Compare non-strings (numbers or other types)
          if (typeof aValue !== 'string' && typeof bValue !== 'string') {
            if (aValue > bValue) return 1;
            if (aValue < bValue) return -1;
            return 0;
          }

          // If one value is a string and the other is not, keep strings first
          if (typeof aValue === 'string') return -1;
          if (typeof bValue === 'string') return 1;

          // Default return for types that are not comparable
          return 0;
        });
      }
      return [...list];
    }, [list, sortBy, isSorted]);

    const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Escape') {
        onSelectTabClick?.();
      }
    };

    useOnClickOutside(menuDownRef, handleOutsideClicked);

    useEffect(() => {
      if (isOpen && dropDownRef.current) {
        dropDownRef.current.focus();
      }
    }, [isOpen]);

    return (
      <div
        className={`${styles['menu-dropdown']} ${wrapperClasses}`}
        ref={menuDownRef}
      >
        {isShowSelectElement && (
          <button
            onClick={handleSelectTabClick}
            className={`${styles['menu-dropdown__select-btn']} ${
              isDisabled ? styles['menu-dropdown__select-btn--disabled'] : ''
            }  `}
            aria-controls='dropdown-list'
            aria-haspopup='listbox'
            aria-expanded={isOpen}
            disabled={isDisabled}
          >
            <TranslationIcon />
            <div
              className={`${styles['menu-dropdown__select-btn__caret-icon']} ${
                isOpen
                  ? styles['menu-dropdown__select-btn__caret-icon--up']
                  : ''
              }`}
            >
              <Caret />
            </div>
          </button>
        )}

        {isOpen && list.length && (
          <div
            id='dropdown-list'
            role='listbox'
            tabIndex={-1}
            className={`${styles['menu-dropdown__drop-down-wrapper']} ${
              isShowSelectElement
                ? ''
                : styles['menu-dropdown__drop-down-regular']
            } ${className}`}
            ref={dropDownRef}
            onKeyDown={handleKeyDown}
          >
            <DropDownRenderer
              selectedValue={selectedValue}
              list={sortedList}
              groupingConfig={groupingConfig}
              groupTooltips={groupTooltips}
              valueProperty={valueProperty}
              unCategorizedListTitle={unCategorizedListTitle}
              onChange={handleMenuDropDownChange}
              multiSelect={multiSelect}
              searchInputPlaceholder={searchInputPlaceholder}
              categorizedDropdown={categorizedDropdown}
              enableSelectAll={enableSelectAll}
              isSearchable={isSearchable}
            />
          </div>
        )}
      </div>
    );
  }
);

MenuDropDown.displayName = 'MenuDropDown';

export default MenuDropDown;
